import { DatePipe } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { TranslationService } from '@core/translation.service';
import { currentCollaborationId, isViewOnlyCustomer } from '@features/customer/store/work-with-customer.signals';
import { AppointmentsApiService } from '@features/listing-details/components/listing-appointments/services/appointments.api.service';
import { DATE_PIPE_DATE_FORMAT, REMOVED_NOTIFICATIONS } from '@features/notifications/constants/notification-constants';
import { AppointmentNotification } from '@features/notifications/models/appointment-notification';
import { AppointmentStatus } from '@features/notifications/models/appointment-status';
import { BaseNotification } from '@features/notifications/models/base/base-notification';
import { CustomerBaseNotification } from '@features/notifications/models/base/customer-base-notification';
import { ListingBaseNotification } from '@features/notifications/models/base/listing-base-notification';
import { CustomerNotification } from '@features/notifications/models/customer-notification';
import { ListingNotification } from '@features/notifications/models/listing.notification';
import { MessageNotification } from '@features/notifications/models/message-notification';
import { NewMatchesNotification } from '@features/notifications/models/new-matches-notification';
import { NotificationItem } from '@features/notifications/models/notification-item';
import dayjs from 'dayjs';
import {
    ADDRESS_SUBTITLE_NOTIFICATIONS,
    ALWAYS_AGENT_NOTIFICATION,
    CUSTOMER_NOTIFICATIONS,
    DATE_NOTIFICATIONS,
    NOTIFICATION_ICON_MAP,
    NOTIFICATION_TITLE_MAP,
    REACTION_TITLE_MAP,
    REDIRECT_LINKS_MAP,
    REGISTERED_NOTIFICATIONS,
    REPORT_ENTITY_TYPE_MAP,
    SELECT_CUSTOMER_ON_REDIRECT_NOTIFICATIONS,
    SUBTITLE_FIELD_MAP,
    SUBTITLE_ICON_CLASS_MAP,
    SUBTITLE_ICON_MAP,
    SUBTITLE_TEXT_MAP,
} from './constants/notification-mapping.constants';

import { EventAction } from 'src/.graphql/types';
import { locale as english } from './i18n/en';

@Injectable({ providedIn: 'root' })
export class NotificationsMappingService {
    private readonly translationService = inject(TranslationService);
    private readonly appointmentsApiService = inject(AppointmentsApiService);
    private readonly datePipe = new DatePipe('en-US');

    private readonly isCorcoran = signal<boolean>(false);

    private readonly notificationDateFormat = 'MM/dd/yyyy | hh:mm a';
    private readonly openHouseDateFormat = 'MM/dd/yyyy | HH:mm';

    constructor() {
        this.translationService.loadTranslations(english);
    }

    public mapAppointmentNotification(notification: AppointmentNotification): NotificationItem {
        const additionalFields: Partial<NotificationItem> = notification.appointment?.agentStatus === AppointmentStatus.Confirmed &&
            notification.appointment?.customerStatus === AppointmentStatus.Confirmed
            ? {
                text: this.translationService.instant('NOTIFICATION_MAPPING.APPOINTMENTS.CONFIRMED'),
                textIconName: 'circle-check',
                textClassName: 'rp-notification-item-info-state--confirmed'
            } : notification.appointment?.agentStatus === AppointmentStatus.Declined ||
                notification.appointment?.customerStatus === AppointmentStatus.Declined ||
                dayjs((notification.appointment?.startDateTime as string | undefined)?.replace('Z', '')).isBefore(dayjs())
                ? {
                    text: this.translationService.instant('NOTIFICATION_MAPPING.APPOINTMENTS.DECLINED'),
                    textIconName: 'circle-x',
                    textClassName: 'rp-notification-item-info-state--declined'
                }
                : this.getAppointmentActions(notification);

        return {
            ...this.mapCommonNotification(notification),
            ...additionalFields,
            date: this.datePipe.transform(
                (notification.appointment?.startDateTime as string | undefined)?.replace('Z', ''),
                this.notificationDateFormat
            ) ?? undefined,
            dateIcon: notification.action === 'APPOINTMENT_UPDATED' ? 'new-arrow-alt-right' : null,
        };
    }

    public mapCommentNotification(notification: MessageNotification): NotificationItem {
        return {
            ...this.mapCommonNotification(notification),
            text: notification.comment?.text
        };
    }

    public mapFavoriteListingNotification(notification: ListingNotification): NotificationItem {
        const customerNotification = notification as CustomerBaseNotification;
        const { notificationText } = notification;
        const hasCustomer = !!customerNotification?.customerId;

        const base = this.mapCommonNotification(
            notification,
            notificationText != null ? { notificationText } : undefined
        );

        const title = hasCustomer
            ? base.title
            : notificationText != null
                ? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.FAVORITE_LISTING_AGENT', { notificationText })
                : this.translationService.instant('NOTIFICATION_MAPPING.TITLES.FAVORITE_LISTING_UPDATED');

        return {
            ...base,
            title,
            subtitle: this.formatAddress(notification.listing?.address, notification.listing?.unit),
            isRemovedAccount: hasCustomer ? base.isRemovedAccount : false,
            isAgentNotification: hasCustomer ? base.isAgentNotification : true,
        };
    }

    public mapNewMatchesNotification(notification: NewMatchesNotification, totalCount?: number): NotificationItem {
        const useCreator = this.shouldUseCreatorInfo(notification);

        return {
            ...this.mapCommonNotification(notification, {
                count: notification.newMatchCount,
                name: notification.customerId
                    ? useCreator && notification.userId !== notification.creatorPersonId
                        ? (notification.creatorFullName ?? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT'))
                        : (notification.customerFullName ?? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT'))
                    : this.translationService.instant('NOTIFICATION_MAPPING.TITLES.YOUR'),
                search: notification.savedSearchName
            }),
            subtitle: undefined,
            subtitleIconName: undefined,
            subtitleIconClass: undefined,
            totalCount
        };
    }

    // eslint-disable-next-line complexity
    public mapCommonNotification(notification: BaseNotification, translationParameters?: object): NotificationItem {
        const customerNotification = notification as CustomerBaseNotification;
        const useCreator = this.shouldUseCreatorInfo(notification);
        const isCustomerNotification = CUSTOMER_NOTIFICATIONS.includes(notification.action);
        const translationKey = this.getNotificationTitleKey(notification.action);

        const isRemovedAccount = !ALWAYS_AGENT_NOTIFICATION.includes(notification.action) && (useCreator
            ? !notification.creatorFullName
            : !customerNotification?.customerFullName);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const subtitleFromField = SUBTITLE_FIELD_MAP[notification.action] ?? null;

        return {
            id: notification.notificationId,
            entityId: notification.entityId,
            isViewed: notification.isViewed,
            title: translationKey
                ? this.translationService.instant(translationKey, {
                    ...this.getCustomerTranslationParameter(notification as CustomerNotification),
                    ...(translationParameters ?? {}),
                    reaction: (() => {
                        const k = REACTION_TITLE_MAP[notification.action];

                        return k
                            ? this.translationService.instant(k)
                            : null;
                    })(),
                    report: (() => {
                        const fn = REPORT_ENTITY_TYPE_MAP[notification.entityType];

                        return fn
                            ? this.translationService.instant(fn(this.isCorcoran()))
                            : null;
                    })(),
                    date: this.datePipe.transform(notification.entityDate, DATE_PIPE_DATE_FORMAT)
                })
                : null,
            subtitle: notification.notificationText ??
                subtitleFromField ??
                (!ADDRESS_SUBTITLE_NOTIFICATIONS.includes(notification.action) && SUBTITLE_TEXT_MAP[notification.action] != null
                    ? this.translationService.instant(SUBTITLE_TEXT_MAP[notification.action])
                    : this.formatAddress(
                        (notification as ListingBaseNotification)?.listing?.address,
                        (notification as ListingBaseNotification)?.listing?.unit
                    )),
            isCustomer: isCustomerNotification,
            collaborationId: notification.collaborationId,
            customerId: customerNotification?.customerId,
            userId: notification.userId,
            notificationDate: notification.createDate,
            notificationIconName: NOTIFICATION_ICON_MAP[notification.action],
            subtitleIconName: SUBTITLE_ICON_MAP[notification.action],
            subtitleIconClass: SUBTITLE_ICON_CLASS_MAP[notification.action],
            isRegistered: REGISTERED_NOTIFICATIONS.includes(notification.action),
            isCollaboration: notification.collaborationId != null && notification.collaborationId !== 0,
            redirectUrl: REDIRECT_LINKS_MAP[notification.action]
                ? REDIRECT_LINKS_MAP[notification.action](notification) ?? undefined : undefined,
            selectsCustomerOnRedirect: SELECT_CUSTOMER_ON_REDIRECT_NOTIFICATIONS.includes(notification.action),
            customerProfileUrl: this.getCustomerProfileUrl(notification),
            date: DATE_NOTIFICATIONS.includes(notification.action)
                ? this.datePipe.transform(
                    notification.entityDate,
                    notification.action === 'OPEN_HOUSE_CREATED' ? this.openHouseDateFormat : this.notificationDateFormat
                ) ?? undefined
                : undefined,
            imageUrl: useCreator ? notification.creatorAvatarUrl : customerNotification?.avatarUrl,
            fullName: useCreator
                ? (notification.creatorFullName ?? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT'))
                : (customerNotification?.customerFullName ?? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT')),
            firstName: (useCreator
                ? notification.creatorFirstName
                : customerNotification?.customerFirstName) || '',
            lastName: (useCreator
                ? notification.creatorLastName
                : customerNotification?.customerLastName) || '',
            primaryCustomerAvatarUrl: isCustomerNotification ? customerNotification?.avatarUrl : undefined,
            primaryCustomerFirstName: isCustomerNotification ? customerNotification?.customerFirstName : undefined,
            primaryCustomerLastName: isCustomerNotification ? customerNotification?.customerLastName : undefined,
            isOpenHouse: notification.action === 'OPEN_HOUSE_CREATED',
            isNewMatch: notification.action === 'NEW_MATCH_ADDED',
            isRemovedShare: notification.action === 'CUSTOMER_SHARE_DELETED',
            isRemovedListing: notification.action === 'LISTING_HARD_DELETED',
            isRemovedAction: REMOVED_NOTIFICATIONS.includes(notification.action),
            listingId: notification.listingId,
            groupTitle: this.getGroupTitle(notification),
            isRemovedAccount,
            isAgentNotification: (!customerNotification?.customerId && !isRemovedAccount) ||
                ALWAYS_AGENT_NOTIFICATION.includes(notification.action)
        };
    }

    private getAppointmentActions(notification: AppointmentNotification): Partial<NotificationItem> {
        if (isViewOnlyCustomer() && notification.collaborationId === currentCollaborationId()) {
            return {};
        }

        return {
            actions: [
                {
                    action: () => {
                        void this.appointmentsApiService.confirmAppointment(notification.entityId);
                    },
                    title: this.translationService.instant('NOTIFICATION_MAPPING.APPOINTMENTS.CONFIRM'),
                    iconName: 'circle-check',
                    customClass: 'rp-appointment-approve'
                },
                {
                    action: () => {
                        void this.appointmentsApiService.declineAppointment(notification.entityId);
                    },
                    title: this.translationService.instant('NOTIFICATION_MAPPING.APPOINTMENTS.REJECT'),
                    iconName: 'circle-x',
                    customClass: 'rp-appointment-decline'
                },
            ]
        };
    }

    private getCustomerTranslationParameter(notification: CustomerNotification): object {
        const useCreator = this.shouldUseCreatorInfo(notification);
        const removedAccount = this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT');

        const displayName = useCreator
            ? (notification.creatorFullName ?? removedAccount)
            : (notification.customerFullName ?? removedAccount);

        return {
            name: displayName,
            primaryCustomerName: notification.customerFullName ?? removedAccount,
            childName: notification.childCustomerFullName ?? removedAccount,
            agentName: notification.agentFullName ?? removedAccount,
        };
    }

    private getGroupTitle(notification: BaseNotification): string | null {
        if (CUSTOMER_NOTIFICATIONS.includes(notification.action)) {
            const customerNotification = notification as CustomerBaseNotification;

            return customerNotification.customerFullName ?? this.translationService.instant('NOTIFICATION_MAPPING.TITLES.REMOVED_ACCOUNT');
        }

        if (notification.action === 'NEW_MATCH_ADDED') {
            return this.translationService.instant('NOTIFICATION_MAPPING.TITLES.NEW_MATCHES');
        }

        if (notification.action === 'OPEN_HOUSE_CREATED') {
            return this.translationService.instant('NOTIFICATION_MAPPING.TITLES.OPEN_HOUSES');
        }

        return null;
    }

    private getNotificationTitleKey(action: EventAction): string | undefined {
        if (action === 'MARKET_ACTIVITY_REPORT_GENERATED') {
            return 'NOTIFICATION_MAPPING.TITLES.MARKET_ACTIVITY';
        }

        return NOTIFICATION_TITLE_MAP[action as keyof typeof NOTIFICATION_TITLE_MAP];
    }

    private shouldUseCreatorInfo(notification: BaseNotification): boolean {
        const customerNotification = notification as CustomerBaseNotification;

        if (notification.creatorPersonId == null) {
            return false;
        }

        if (customerNotification?.customerId == null) {
            return true;
        }

        return notification.creatorPersonId !== customerNotification.customerId;
    }

    private getCustomerProfileUrl(notification: BaseNotification): string | null {
        const customerNotification = notification as CustomerBaseNotification;

        if (
            (CUSTOMER_NOTIFICATIONS.includes(notification.action) || notification.action === 'NEW_MATCH_ADDED') &&
            customerNotification?.customerId) {
            return `customer/${customerNotification.customerId}`;
        }

        return null;
    }

    private formatAddress(address?: string | null, unit?: string | null): string {
        return [address, unit].filter(Boolean).join(', ');
    }
}
